import { isFulfilled } from "@reduxjs/toolkit";
import { useFormik } from "formik";
import { useMemo } from "react";
import DefaultImg from "../../../../assets/img/default-image.svg";
import { NOTIFICATION_TIME_OUT } from "../../../../constants/app";
import Roles from "../../../../models/roles";
import { formatDate } from "../../../../models/vacancy";
import {
  useAppDispatch,
  useAppSelector as useSelector,
} from "../../../../store";
import {
  hideTransparentPreloaderAction,
  showNotificationAction,
  showTransparentPreloaderAction,
} from "../../../../store/application/slice";
import { approvePayRollRate, sendOverdueTimesheetsToPayroll } from "../../../../store/timesheet/thunks";
import { ITimesheetData } from "../../../../store/timesheet/types";
import { createValidationSchema } from "../../../../utils/createValidationSchema";
import { extractSuburbFromAddress } from "../../../../utils/stringUtils";
import { getNextMonday } from "../../../../utils/timeHelper";
import { calculateBreakDuration } from "../../../Client/Timesheet/ReviewTimesheet";
import CustomButton from "../../../UI/Buttons/CustomButton";
import Clock from "../../../UI/Icons/Clock";
import RoundedTick from "../../../UI/Icons/RoundedTick";
import Warning from "../../../UI/Icons/Warning";
import CurrencyInput from "../../../UI/Inputs/CurrencyInput";
import StarRating from "../../../UI/StarRating";
import ErrorMessage from "../../../UI/Tooltips/ErrorMessage";

interface ViewTimesheetProps {
  timesheet: ITimesheetData;
  timesheetType?: string;
  fetchPendingTImesheets?: () => void;
  setShowViewTimesheetModal?: (visible: boolean) => void;
}

const ViewTimesheet: React.FC<ViewTimesheetProps> = ({
  timesheet,
  timesheetType,
  fetchPendingTImesheets,
  setShowViewTimesheetModal,
}) => {
  const dispatch = useAppDispatch();

  const isApproved = timesheet?.approver ? true : false;

  const InProgress = useSelector((state) => state.timesheet.inProgress);
  const user = useSelector((state) => state.auth.user);

  const isAdmin = useMemo(
    () =>
      user?.role.name === Roles.GENERAL_ADMIN ||
      user?.role.name === Roles.SUPER_ADMIN,
    [user]
  );

  const minBaseRate =
    timesheet.vacancy !== null && timesheet.vacancy?.matching_position !== null
      ? timesheet.vacancy?.matching_position?.min_base_rate
      : null;
  const minChargeRate =
    timesheet.vacancy !== null && timesheet.vacancy?.matching_position !== null
      ? timesheet.vacancy?.matching_position?.min_charge_rate
      : null;

  const minBRate = minBaseRate !== null ? minBaseRate : "31.19";
  const minCRate = minChargeRate !== null ? minChargeRate : "45.78";

  const { handleSubmit, values, errors, handleChange } = useFormik({
    validationSchema: createValidationSchema(
      "approve_timesheet",
      "",
      "",
      minBRate ?? "0",
      minCRate ?? "0"
    ),
    validateOnBlur: false,
    validateOnChange: false,
    initialValues: {
      loading_base_rate: 0,
      loading_rate: timesheet?.candidate?.profile?.loading_amount || 0,
      loading_charge_rate: 0,
      allowance_base_rate: timesheet?.vacancy?.allowance || 0,
      allowance_charge_rate: 0,
      id: timesheet.id,
      did_not_work: timesheet?.did_not_work || false,
    },
    onSubmit: async (values: any) => {
      handlePayrollSubmit();
    },
  });



  const handlePayrollSubmit = async () => {
    try {
      dispatch(showTransparentPreloaderAction());
      
      // For regular timesheets, approve for scheduled payroll
      const result = await dispatch(approvePayRollRate({ ...values }));
        if (isFulfilled(result)) {
          setShowViewTimesheetModal && setShowViewTimesheetModal(false);
          timesheet?.timesheet_status !== "Overdue" && dispatch(showNotificationAction("Scheduled"));          
        }

      // For overdue timesheets, send to payroll immediately
      if (timesheet?.timesheet_status === "Overdue") {
        const result = await dispatch(
          sendOverdueTimesheetsToPayroll({ 
            timesheet_ids: [timesheet.id] 
          })
        );
        
        if (isFulfilled(result)) {
          setShowViewTimesheetModal && setShowViewTimesheetModal(false);
          timesheet?.timesheet_status === "Overdue" && dispatch(showNotificationAction("Sent"));
          setTimeout(() => {
            fetchPendingTImesheets && fetchPendingTImesheets();
          }, NOTIFICATION_TIME_OUT);
        } else {
          // Handle error from API
          const error = result.payload as any;
          const errorMessage = error?.message || "Failed to send overdue timesheet to payroll";
          dispatch(showNotificationAction(errorMessage));
        }
      } 

      setTimeout(() => {
        fetchPendingTImesheets && fetchPendingTImesheets();
      }, NOTIFICATION_TIME_OUT);
    } catch (e) {
      console.error(e);
    } finally {
      dispatch(hideTransparentPreloaderAction());
    }
  };


  return (
    <div className="d-flex flex-column gap-15">
      <div className="fs-22 fw-400 text-black">
        {timesheetType === "payroll-pending" && timesheet?.status_id !== 3 
          ? "Review Timesheet"
          : "View Timesheet"}
      </div>
      <div className="bg-off-white h-1px"></div>
      <div className="d-flex flex-column gap-15 mobile-job-scroll">
        <div className="d-flex gap-20">
          <div className="w-50px h-50px  border-2 border-grey-dark radius-40px">
            <img
              src={timesheet?.candidate?.photo || DefaultImg}
              alt="candidate"
              width={50}
              height={50}
              className="img-responsive rounded-circle object-fit-cover"
            />
          </div>
          <div className="flex-grow-1 d-flex flex-column gap-20">
            <div className="flex-grow-1 d-flex flex-column gap-3 text-break">
              <div className="fs-16 fw-700 text-black">
                {timesheet?.candidate?.first_name}{" "}
                {timesheet?.candidate?.last_name}
              </div>
              <StarRating
                rating={
                  isAdmin
                    ? timesheet?.rating?.average_rating || 0
                    : timesheet?.rating?.latest_rating ||
                      timesheet?.rating?.average_rating ||
                      0
                }
                color={
                  isAdmin || !timesheet?.rating?.latest_rating
                    ? "#aaaaaa"
                    : "#51C2B9"
                }
              />
              {!isAdmin &&
                !timesheet?.rating?.latest_rating &&
                timesheet?.rating?.average_rating && (
                  <div className="fs-10 fw-400 text-grey-dark">
                    Average Rating
                  </div>
                )}
              {/* <StarRating rating={timesheet?.rating?.average_rating || 0} /> */}
            </div>
          </div>
        </div>
        <div className="d-flex gap-20">
          <div className="w-50px h-50px  border-2 border-grey-dark radius-40px">
            <img
              src={timesheet?.centre?.photo || DefaultImg}
              alt="centre"
              width={50}
              height={50}
              className="img-responsive rounded-circle object-fit-cover"
            />
          </div>
          <div className="flex-grow-1 d-flex flex-column gap-20">
            <div className="flex-grow-1 d-flex flex-column gap-3 text-break">
              <div className="fs-16 fw-700 text-black">
                {timesheet?.centre?.business_name},{" "}
                <span className="fw-400">{extractSuburbFromAddress(timesheet?.centre?.address)}</span>
              </div>
              <div className="fs-13 fw-400 text-black">
                {timesheet?.vacancy?.matching_position?.name ||
                  timesheet?.position_name}
              </div>
            </div>
            <div className="d-flex flex-column gap-3 fs-13 text-black">
              <div className="fw-700">Job Timeframe</div>
              <div className="fw-400">{timesheet?.timeframe}</div>
            </div>
          </div>
        </div>
        {!isApproved && !timesheet?.did_not_work && !timesheet?.is_declined_timesheet && (
          <>
            <div className="h-1px"></div>
            <div className="d-flex gap-10 align-items-center">
              <div className="fs-16 fw-700 text-black">Status:</div>
              <div className="border border-alert radius-8px px-8px py-4px fs-13 fw-400 text-alert">
                Pending Approval
              </div>
            </div>
          </>
        )}
        
        {timesheet?.is_declined_timesheet && timesheet?.decline_reason?.message && (
          <>
            <div className="h-1px"></div>
            <div className="d-flex flex-column gap-10">
              <div className="d-flex flex-column flex-md-row gap-10 align-items-md-center align-items-start">
                <div className="fs-16 fw-700 text-black">Status:</div>
                <div className="border border-alert radius-8px px-8px py-4px fs-13 fw-400 text-alert">
                  Declined
                </div>
                <div className="fs-13 text-grey-medium">{timesheet?.declined_info ?? "Nodata"}</div>
              </div>
              <div className="d-flex flex-column gap-5">
                <div className="fs-16 fw-700 text-black">Centre Comment:</div>
                <div className="fs-16 fw-400 text-black">{timesheet.decline_reason.message}</div>
                {timesheet.decline_reason.created_on && (
                  <div className="fs-12 fw-400 text-grey-medium">
                    Declined on {formatDate(new Date(timesheet.decline_reason.created_on))}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
        
        <div className="h-1px"></div>
        {
          timesheet?.is_declined_timesheet && !isApproved ? (
            <div className="fs-16 fw-700 text-black">Timesheet details that were submitted and declined:</div>
          ) : (
             <div className="fs-16 fw-700 text-black">
              {isApproved ? "Approved" : "Timesheet"} details of the above job:
            </div>
          )
        }
        {
          timesheet?.did_not_work && (
            <div className="fs-16">They did not work on this day</div>
          )
        }

        {
          !timesheet?.did_not_work && (
            <div className="d-flex gap-50 gap-mobile-20">
              <div className="d-flex flex-column gap-5 fw-400">
                <div className="fs-13 text-grey-medium">Start time</div>
                <div className="fs-16 text-black">{timesheet.job_start_time}</div>
              </div>
              <div className="d-flex flex-column gap-5 fw-400">
                <div className="fs-13 text-grey-medium">End time</div>
                <div className="fs-16 text-black">{timesheet.job_end_time}</div>
              </div>
              <div className="d-flex flex-column gap-5 fw-400">
                <div className="fs-13 text-grey-medium">Break</div>
                <div className="fs-16 text-black">
                  {calculateBreakDuration(
                    timesheet.break_start_time,
                    timesheet.break_end_time
                  )}
                </div>
              </div>
            </div>
          )
        }
        
        {isApproved && (
          <>
            <div className="h-1px"></div>
            <div className="fs-16 fw-700 text-black">Approver information:</div>
            <div className="d-flex gap-40 gap-mobile-20 flex-wrap">
              <div className="d-flex flex-column gap-5 fw-400">
                <div className="fs-13 text-grey-medium">User Name</div>
                <div className="fs-16 text-black">
                  {timesheet?.approver?.first_name}{" "}
                  {timesheet?.approver?.last_name}
                </div>
              </div>
              <div className="d-flex flex-column gap-5 fw-400">
                <div className="fs-13 text-grey-medium">User Type</div>
                <div className="fs-16 text-black">Centre Admin</div>
              </div>
              <div className="d-flex flex-column gap-5 fw-400">
                <div className="fs-13 text-grey-medium">IP Address</div>
                <div className="fs-16 text-black">{timesheet?.approver_ip}</div>
              </div>
              <div className="d-flex flex-column gap-5 fw-400">
                <div className="fs-13 text-grey-medium">
                  Approval Date and Time
                </div>
                <div className="fs-16 text-black">{timesheet?.approved_on}</div>
              </div>
            </div>
          </>
        )}
        {isApproved && isAdmin && timesheetType === "payroll-pending" && timesheet?.status_id !== 3 && (
          <>
            <div className="min-h-1px"></div>
            <div className="bg-off-white min-h-1px"></div>
            <div className="min-h-1px"></div>
            <div className="fs-16 fw-700 text-black">Payroll rates:</div>
            <div className="fs-13 fw-700">
              {minBaseRate !== null && minChargeRate !== null
                ? `Job noted as ${
                    timesheet.vacancy?.matching_position.min_base_rate
                      ? timesheet.vacancy?.matching_position.min_base_rate
                      : "0.00"
                  }-${
                    timesheet.vacancy?.matching_position.max_base_rate
                      ? timesheet.vacancy?.matching_position.max_base_rate
                      : "0.00"
                  }/hr`
                : ""}
            </div>
            <div className="d-flex flex-column flex-lg-row gap-10">
              {values.loading_rate > 0 ? (
                <div className="d-flex flex-column w-100 gap-5">
                  <CurrencyInput
                    field={"loading_rate"}
                    onChange={handleChange}
                    label="Added Loading"
                    value={String(values.loading_rate || 0)}
                    disabled={true}
                  />
                  {errors && (
                    <ErrorMessage message={errors.loading_rate as string} />
                  )}
                </div>
              ) : (
                ""
              )}

              <div className="d-flex flex-column w-100 gap-5">
                <CurrencyInput
                  field={"loading_base_rate"}
                  onChange={handleChange}
                  label="Base Rate (Per Hour)"
                  value={String(values.loading_base_rate || 0)}
                />
                <div className="fs-13 fw-400 text-grey-medium">
                  Minimum ${minBRate}
                </div>
                {errors && (
                  <ErrorMessage message={errors.loading_base_rate as string} />
                )}
              </div>

              <div className="d-flex flex-column w-100 gap-5">
                <CurrencyInput
                  field={"loading_charge_rate"}
                  onChange={handleChange}
                  label="Charge Rate (Per Hour)"
                  value={String(values.loading_charge_rate || 0)}
                />
                <div className="fs-13 fw-400 text-grey-medium">
                  Minimum ${minCRate}
                </div>
                {errors && (
                  <ErrorMessage
                    message={errors.loading_charge_rate as string}
                  />
                )}
              </div>
            </div>

            {values.allowance_base_rate > 0 ? (
              <>
                <div className="fs-16 fw-700 text-black">Allowance rates:</div>
                {timesheet.vacancy && timesheet.vacancy?.allowance !== null ? (
                  <div className="fs-13 fw-700">
                    Job noted as extra ${timesheet.vacancy?.allowance}/day
                    allowance
                  </div>
                ) : (
                  "0.00"
                )}
                <div className="d-flex flex-column flex-lg-row gap-10">
                  <div className="d-flex flex-column w-100 gap-5">
                    <CurrencyInput
                      field={"allowance_base_rate"}
                      onChange={handleChange}
                      label="Allowance Base (Per Day)"
                      value={String(values.allowance_base_rate || 0)}
                      disabled={true}
                    />
                    {errors && (
                      <ErrorMessage
                        message={errors.allowance_base_rate as string}
                      />
                    )}
                  </div>

                  <div className="d-flex flex-column w-100 gap-5">
                    <CurrencyInput
                      field={"allowance_charge_rate"}
                      onChange={handleChange}
                      label="Allowance Charge (Per Day)"
                      value={String(values.allowance_charge_rate || 0)}
                    />
                    {errors && (
                      <ErrorMessage
                        message={errors.allowance_charge_rate as string}
                      />
                    )}
                  </div>

                  <div className="w-100"></div>
                </div>
              </>
            ) : (
              ""
            )}

            <div className="d-flex flex-column flex-md-row justify-content-md-between align-items-md-center gap-10">

              {
                timesheet?.timesheet_status === "Overdue" ? (
                  <div className="d-flex gap-10  align-items-center"><Warning /> <div className="fs-16">Send overdue payroll <span className="fw-700">immediately</span></div></div>
                ) : (
                  <div className="d-flex gap-10 align-items-center"><Warning /> <div className="fs-16">Schedule to send on <span className="fw-700">{getNextMonday("short")} 5pm</span></div></div>
                )
              }

              <CustomButton
                variant="primary"
                type="button"
                className="btn-theme bg-teal text-black mobile-w-100"
                disabled={!isApproved}
                isLoading={InProgress}
                onClick={handleSubmit}
              >
                
                <Clock color={InProgress || !isApproved ? "#aaaaaa" : "black"} size={20}/>
                {timesheet?.timesheet_status === "Overdue" ? "Send To Payroll Now" : "Schedule For Payroll"}
              </CustomButton>
            </div>
          </>
        )}

       
        {
          isAdmin && (timesheetType === "sent-to-payroll" || timesheetType === "payroll-pending") && !timesheet?.did_not_work && timesheet?.status_id === 3 &&  (
            <>
              <div className="h-1px"></div>
              <div className="fs-16 fw-700 text-black">Payroll and allowance rates:</div>
              <div className="d-flex gap-40 gap-mobile-20 flex-wrap">
                <div className="d-flex flex-column gap-5 fw-400">
                  <div className="fs-13 text-grey-medium">Base Rate</div>
                  <div className="fs-16 text-black">${timesheet?.loading_base_rate || 0} per hour</div>
                </div>
                <div className="d-flex flex-column gap-5 fw-400">
                  <div className="fs-13 text-grey-medium">Charge Rate</div>
                  <div className="fs-16 text-black">${timesheet?.loading_charge_rate || 0} per hour</div>
                </div>
                <div className="d-flex flex-column gap-5 fw-400">
                  <div className="fs-13 text-grey-medium">Allowance Base</div>
                  <div className="fs-16 text-black">${timesheet?.allowance_base_rate || 0} per day</div>
                </div>
                <div className="d-flex flex-column gap-5 fw-400">
                  <div className="fs-13 text-grey-medium"> Allowance Charge </div>
                  <div className="fs-16 text-black">${timesheet?.allowance_charge_rate || 0} per day</div>
                </div>
              </div>
             </>
          )
        }

        {
          isAdmin && (timesheetType === "sent-to-payroll" || timesheetType === "payroll-pending") && !timesheet?.did_not_work &&  timesheet?.status_id === 3 && (
            <>
            <div className="bg-off-white h-1px"></div>
              <div className="d-flex flex-column flex-md-row gap-10 align-items-md-center align-items-start">
                <div className="fs-16 fw-700 text-black">Status:</div>
                {
                  timesheet?.sent_to_payroll_on !== null ? (
                    <>
                      <div className="border border-teal-dark px-8px py-4px text-teal-dark fs-13 radius-8px">Sent to Payroll</div>
                      <div className="fs-13 text-grey-medium">Sent {formatDate(timesheet?.sent_to_payroll_on ? new Date(timesheet.sent_to_payroll_on) : null)}</div>
                    </>
                  ) : (
                     <>
                     <div className="border border-alert px-8px py-4px text-alert fs-13 radius-8px ">Scheduled for Payroll</div>
                     <div className="fs-13 text-grey-medium">Sending {getNextMonday()}</div>
                     </>
                  )
                }
                
              </div>
            </>
          )
        }
              
           

        {
           !!timesheet?.did_not_work && isAdmin && timesheetType === "payroll-pending" && timesheet?.status_id !== 3  && (
            <div className="d-flex flex-column flex-md-row justify-content-md-between align-items-md-center gap-10">
               {
                timesheet?.timesheet_status === "Overdue" ? (
                  <div className="d-flex gap-10  align-items-center"><Warning /> <div className="fs-16">Send overdue payroll <span className="fw-700">immediately</span></div></div>
                ) : (
                  <div className="d-flex gap-10 align-items-center"><Warning /> <div>Schedule to send on <span className="fw-700">{getNextMonday("short")} 5pm</span></div></div>
                )
              }
              <CustomButton
                variant="primary"
                type="button"
                className="btn-theme bg-teal text-black text-nowrap"                
                isLoading={InProgress}
                onClick={handleSubmit}
              >
                {
                  timesheet?.timesheet_status === "Overdue" ? (
                    <RoundedTick />
                  ) : (
                    <Clock color={InProgress ? "#aaaaaa" : "black"} size={20}/>
                  )
                }
                {timesheet?.timesheet_status === "Overdue" ? "Send To Payroll Now" : "Schedule For Payroll"}
              </CustomButton>
            </div>
           )
        }
        

        
      </div>
    </div>
  );
};

export default ViewTimesheet;
